import { z } from 'zod'

import { base } from '@/lib/orpc/errors'
import {
  addressSchema,
  assetSelectorSchema,
  chainIdSchema,
  cursorSchema,
  hexIdSchema,
  marketWarningSchema,
  pageOutput,
  uint256StringSchema
} from '@/lib/orpc/schemas'

/** The bridge's own bound on every list filter. */
const MAX_FILTER_VALUES = 100

const usdBound = z.number().nonnegative()

export const MARKET_LIST_ORDER_BY = [
  'loanAssetSymbol',
  'collateralAssetSymbol',
  'lltv',
  'sizeUsd',
  'totalLiquidityUsd',
  'utilization',
  'supplyAssetsUsd',
  'borrowAssetsUsd',
  'liquidityAssetsUsd',
  'weeklyBorrowApy',
  'apyAtTarget',
  'oraclePrice',
  'borrowToTargetUsd',
  'netBorrowApy',
  'avgNetBorrowApy',
  'avgNetSupplyApy',
  'collateralAssetsUsd',
  'badDebtUsd',
  'realizedBadDebtUsd'
] as const

export const marketListInputSchema = z
  .strictObject({
    /** Page size; the bridge's default when omitted. */
    limit: z.number().int().min(1).max(1000).optional(),
    /** The `nextCursor` of the previous page, called with the same filters. */
    cursor: cursorSchema.optional(),
    orderBy: z.enum(MARKET_LIST_ORDER_BY).optional(),
    orderDirection: z.enum(['asc', 'desc']).optional(),
    /** Substring of the loan or collateral asset symbol. */
    search: z.string().min(1).max(100).optional(),
    /** Every chain the bridge exposes when omitted. */
    chainIds: z.array(chainIdSchema).min(1).max(MAX_FILTER_VALUES).optional(),
    /** Only these market ids, scoped to `chainIds`. */
    uniqueKeys: z.array(hexIdSchema).min(1).max(MAX_FILTER_VALUES).optional(),
    /** Also return unlisted markets among `uniqueKeys`. */
    includeUnlisted: z.boolean().optional(),
    /** `chainId:address` selectors, each matched on its own chain only. */
    loanAssets: z.array(assetSelectorSchema).max(MAX_FILTER_VALUES).optional(),
    collateralAssets: z.array(assetSelectorSchema).max(MAX_FILTER_VALUES).optional(),
    sizeUsdGte: usdBound.optional(),
    sizeUsdLte: usdBound.optional(),
    totalLiquidityUsdGte: usdBound.optional(),
    totalLiquidityUsdLte: usdBound.optional(),
    /** Inclusive LLTV bounds in WAD (1e18 = 100%). */
    lltvGte: uint256StringSchema.optional(),
    lltvLte: uint256StringSchema.optional(),
    curatorIds: z.array(z.string().min(1).max(100)).max(MAX_FILTER_VALUES).optional(),
    /** Bounds on `avgNetBorrowApy`, as decimal rates. */
    avgNetBorrowApyGte: z.number().optional(),
    avgNetBorrowApyLte: z.number().optional(),
    /** Also return `facets`; null otherwise. */
    includeFacets: z.boolean().optional(),
    /** Also return `countTotal`; null otherwise. */
    countTotal: z.boolean().optional(),
    /** Also return `totalBorrowUsd`; null otherwise. */
    totalBorrowUsd: z.boolean().optional()
  })
  .refine(input => !input.includeUnlisted || !!input.uniqueKeys, {
    message: 'includeUnlisted needs uniqueKeys',
    path: ['includeUnlisted']
  })

export type MarketListInput = z.infer<typeof marketListInputSchema>

const marketAssetSchema = z.strictObject({
  address: addressSchema,
  symbol: z.string(),
  name: z.string().nullable(),
  decimals: z.number().int().nonnegative(),
  logoUri: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  priceUsd: z.number().nullable()
})

/** In loan units. Null when the market state is unavailable. */
const loanUnitsSchema = uint256StringSchema.nullable()
/** Null when the loan asset has no USD price or the state is unavailable, never 0. */
const usdSchema = z.number().nullable()
/** Native: interest only, rewards excluded. Null when the state is unavailable. */
const nativeApySchema = z.number().nullable()

const rewardAssetSchema = z.strictObject({
  chainId: chainIdSchema,
  address: addressSchema,
  symbol: z.string(),
  decimals: z.number().int().nonnegative(),
  logoUri: z.string().nullable()
})

/** Bad debt in loan units, and in USD when the loan asset is priced. */
const badDebtSchema = z.strictObject({ underlying: uint256StringSchema, usd: usdSchema })

const marketListItemSchema = z.strictObject({
  chainId: chainIdSchema,
  marketId: hexIdSchema,
  loanAsset: marketAssetSchema,
  collateralAsset: marketAssetSchema.nullable(),
  irmAddress: addressSchema,
  oracleAddress: addressSchema,
  /** Liquidation LTV in WAD (1e18 = 100%). */
  lltv: uint256StringSchema,
  /** One raw collateral unit in raw loan units, scaled by 1e36. Null when the oracle cannot price. */
  oraclePrice: uint256StringSchema.nullable(),
  /** Supply plus the liquidity the Public Allocator can reallocate in. */
  size: loanUnitsSchema,
  sizeUsd: usdSchema,
  /** `size` minus borrow assets. */
  totalLiquidity: loanUnitsSchema,
  totalLiquidityUsd: usdSchema,
  supplyAssets: loanUnitsSchema,
  supplyAssetsUsd: usdSchema,
  borrowAssets: loanUnitsSchema,
  borrowAssetsUsd: usdSchema,
  liquidityAssets: loanUnitsSchema,
  liquidityAssetsUsd: usdSchema,
  /** What can be borrowed before utilization reaches 90%, Public Allocator liquidity included. */
  borrowToTargetUsd: usdSchema,
  /** In collateral units. Null when the market state is unavailable. */
  collateralAssets: uint256StringSchema.nullable(),
  collateralAssetsUsd: usdSchema,
  /** Borrow over supply, as a decimal ratio. */
  utilization: z.number().nullable(),
  borrowApy: nativeApySchema,
  /** Borrow APY over the last 6 hours. */
  avgBorrowApy: nativeApySchema,
  /** `avgBorrowApy` minus the borrow rewards APR. */
  avgNetBorrowApy: z.number().nullable(),
  /** Supply APY over the last 6 hours, net of the market fee. */
  avgSupplyApy: nativeApySchema,
  /** `avgSupplyApy` plus the supply rewards APR. */
  avgNetSupplyApy: z.number().nullable(),
  /** Instantaneous borrow APY net of the borrow rewards APR. */
  netBorrowApy: z.number().nullable(),
  weeklyBorrowApy: nativeApySchema,
  /** `weeklyBorrowApy` minus the borrow rewards APR. */
  weeklyNetBorrowApy: z.number().nullable(),
  /** Borrow APY at the IRM target utilization. */
  apyAtTarget: nativeApySchema,
  /** The collateral token's own yield APY, as a decimal rate; not part of any net borrow APY. */
  collateralYield: z.number().nullable(),
  /** Rewards-only APRs, as decimal rates. */
  supplyRewards: z.array(z.strictObject({ asset: rewardAssetSchema, supplyApr: z.number() })),
  borrowRewards: z.array(z.strictObject({ asset: rewardAssetSchema, borrowApr: z.number() })),
  /** Paid for holding the collateral asset; never part of the borrow APY. */
  collateralRewards: z.array(
    z.strictObject({ asset: rewardAssetSchema, collateralApr: z.number() })
  ),
  /** Curators whose vaults supply the market, with those vaults. */
  curators: z.array(
    z.strictObject({
      id: z.string(),
      name: z.string(),
      image: z.string().nullable(),
      vaults: z.array(
        z.strictObject({
          chainId: chainIdSchema,
          address: addressSchema,
          name: z.string(),
          imageUri: z.string().nullable(),
          totalAssetsUsd: usdSchema,
          isVaultV2: z.boolean()
        })
      )
    })
  ),
  warnings: z.array(marketWarningSchema),
  badDebt: badDebtSchema,
  realizedBadDebt: badDebtSchema
})

export type MarketListItem = z.infer<typeof marketListItemSchema>

const count = z.number().int().nonnegative()

const assetFacetSchema = z.array(
  z.strictObject({
    chainId: chainIdSchema,
    address: addressSchema,
    count,
    /** Null for an asset the bridge cannot describe. */
    metadata: z
      .strictObject({
        symbol: z.string(),
        logoUri: z.string().nullable(),
        tags: z.array(z.string()).nullable()
      })
      .nullable()
  })
)

/** Per-value counts of the markets matching every other filter, each facet ignoring its own. */
const marketListFacetsSchema = z.strictObject({
  chainIds: z.array(z.strictObject({ value: chainIdSchema, count, sizeUsd: z.number() })),
  loanAssets: assetFacetSchema,
  collateralAssets: assetFacetSchema,
  curatorIds: z.array(
    z.strictObject({
      value: z.string(),
      count,
      metadata: z.strictObject({ name: z.string(), image: z.string().nullable() }).nullable()
    })
  )
})

export type MarketListFacets = z.infer<typeof marketListFacetsSchema>

export const marketListOutputSchema = pageOutput(marketListItemSchema).extend({
  /** Markets matching the filters, across every page. Null unless `countTotal` was asked. */
  countTotal: z.number().int().nonnegative().nullable(),
  /** Borrowed USD across every matching market. Null unless `totalBorrowUsd` was asked. */
  totalBorrowUsd: z.number().nullable(),
  /** Null unless `includeFacets` was asked. */
  facets: marketListFacetsSchema.nullable()
})

export type MarketListOutput = z.infer<typeof marketListOutputSchema>

/** One page of Blue markets, filtered and sorted by the bridge. */
export const marketList = base.input(marketListInputSchema).output(marketListOutputSchema)

/** The bridge's bound on `unique_keys`, which is also the most it returns unlisted markets for. */
export const MAX_MARKET_KEYS = 100

export const marketsByKeysInputSchema = z.strictObject({
  chainId: chainIdSchema,
  marketIds: z.array(hexIdSchema).min(1).max(MAX_MARKET_KEYS)
})

/** @internal */
export type MarketsByKeysInput = z.infer<typeof marketsByKeysInputSchema>

export const marketsByKeysOutputSchema = z.strictObject({
  /** The markets the bridge found, listed or not, in its order; unknown ids are left out. */
  items: z.array(marketListItemSchema),
  /** True when some rows failed validation and were dropped. */
  partial: z.boolean()
})

/** The given markets of one chain, unlisted ones included, in the market list's row shape. */
export const marketsByKeys = base.input(marketsByKeysInputSchema).output(marketsByKeysOutputSchema)
